import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import {
  criarSolicitacao, anexarFotosSolicitacao, listarMinhasSolicitacoes,
  obterSolicitacaoConsumer, aceitarOrcamento, cancelarSolicitacao,
  listarSolicitacoesEmpresa, enviarOrcamento,
  uploadFotoSolicitacao,
} from '@/services/solicitacoes.service';

// ── Cliente ──
export function useMinhasSolicitacoes() {
  return useQuery({ queryKey: ['solicitacoes'], queryFn: listarMinhasSolicitacoes });
}

export function useSolicitacaoConsumer(id: string | undefined) {
  return useQuery({
    queryKey: ['solicitacao', id],
    queryFn: () => obterSolicitacaoConsumer(id as string),
    enabled: !!id,
  });
}

/** Cria o pedido, faz upload das fotos e anexa os paths. Devolve o id do pedido. */
export function useCriarSolicitacao() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (input: { categoriaId: string; cidade: string; descricao: string; fotos: File[] }) => {
      const id = await criarSolicitacao({ categoriaId: input.categoriaId, cidade: input.cidade, descricao: input.descricao });
      if (input.fotos.length > 0) {
        const paths: string[] = [];
        for (const f of input.fotos) paths.push(await uploadFotoSolicitacao(id, f));
        await anexarFotosSolicitacao(id, paths);
      }
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['solicitacoes'] });
      toast.success(t('requests.created'));
    },
    onError: (e) => toast.error(sanitizeSupabaseError(e)),
  });
}

export function useAceitarOrcamento() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (orcamentoId: string) => aceitarOrcamento(orcamentoId),
    onSuccess: (_clienteId, _orcamentoId) => {
      qc.invalidateQueries({ queryKey: ['solicitacoes'] });
      qc.invalidateQueries({ queryKey: ['solicitacao'] });
      toast.success(t('requests.accepted'));
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : '';
      if (msg.includes('nao_aberta')) { toast.error(t('requests.errNotOpen')); return; }
      if (msg.includes('nao_dono')) { toast.error(t('requests.errNotOwner')); return; }
      toast.error(sanitizeSupabaseError(e));
    },
  });
}

export function useCancelarSolicitacao() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => cancelarSolicitacao(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['solicitacoes'] });
      qc.invalidateQueries({ queryKey: ['solicitacao'] });
      toast.success(t('requests.cancelled'));
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : '';
      if (msg.includes('nao_aberta')) { toast.error(t('requests.errNotOpen')); return; }
      toast.error(sanitizeSupabaseError(e));
    },
  });
}

// ── Empresa ──
export function useSolicitacoesEmpresa() {
  return useQuery({ queryKey: ['solicitacoes-empresa'], queryFn: listarSolicitacoesEmpresa });
}

export function useEnviarOrcamento() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (input: { solicitacaoId: string; valor: number; mensagem?: string }) => enviarOrcamento(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['solicitacoes-empresa'] });
      toast.success(t('solicitacoes.quoteSent'));
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : '';
      if (msg.includes('ja_orcado')) { toast.error(t('solicitacoes.errAlreadyQuoted')); return; }
      if (msg.includes('nao_elegivel')) { toast.error(t('solicitacoes.errNotEligible')); return; }
      toast.error(sanitizeSupabaseError(e));
    },
  });
}
